import { useEffect, useRef } from 'react';

/**
 * Re-reads a screen on a timer, so a level cleared by someone else appears
 * without anyone pressing refresh.
 *
 * Two things it deliberately does not do: it stops once the request is
 * settled, and it holds still while the tab is in the background -- an inbox
 * left open overnight should not spend the night asking. Returning to the tab
 * reads once immediately, because that is the moment the answer is wanted.
 */
export interface AutoRefreshOptions {
  /** Poll only while this is true; a decided request needs no watching. */
  readonly active: boolean;
  readonly everyMs?: number;
}

export function useAutoRefresh(
  refresh: () => void | Promise<void>,
  { active, everyMs = 10_000 }: AutoRefreshOptions,
): void {
  // Held in a ref so a new closure each render does not restart the timer.
  const latest = useRef(refresh);
  useEffect(() => {
    latest.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!active) return;

    const hidden = (): boolean =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const id = window.setInterval(() => {
      if (!hidden()) void latest.current();
    }, everyMs);

    const onVisible = (): void => {
      if (!hidden()) void latest.current();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, everyMs]);
}
